import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import PropLabelOrderQuizPreview from "../../features/previews/prop_label_quiz/PropLabelOrderQuizPreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import {
  CreatorPropLabelPreferredSide,
  IPropLabelQuiz,
  IPropLabelQuizAnswer,
  PropLabelQuizPreferredSide,
} from "./PropLabelQuiz";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./SearchableInteractionValues";

const defaultDistanceBias = 1;
const minDistanceBias = 0.5;
const maxDistanceBias = 3;

const isPropLabelPreferredSide = (
  value: unknown,
): value is PropLabelQuizPreferredSide =>
  Object.values(CreatorPropLabelPreferredSide).includes(
    value as CreatorPropLabelPreferredSide,
  );

const clampDistanceBias = (value: number) =>
  Math.min(Math.max(value, minDistanceBias), maxDistanceBias);

const getDistanceBias = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value)
    ? clampDistanceBias(value)
    : defaultDistanceBias;

export interface IPropLabelOrderQuiz extends IPropLabelQuiz {}

const createAnswer = (text: string): IPropLabelQuizAnswer => ({
  id: GetNewGuid(),
  correct: false,
  text,
  propGuid: "",
  tag: "",
  preferredSide: CreatorPropLabelPreferredSide.Auto,
  distanceBias: defaultDistanceBias,
});

const migrateAnswer = (
  answer: Partial<IPropLabelQuizAnswer>,
  fallbackText: string,
): IPropLabelQuizAnswer => ({
  id: answer.id ?? GetNewGuid(),
  correct: false,
  text: answer.text ?? fallbackText,
  propGuid: answer.propGuid ?? "",
  tag: answer.tag ?? "",
  inspectDefinitionId:
    typeof answer.inspectDefinitionId === "string" &&
    answer.inspectDefinitionId.length > 0
      ? answer.inspectDefinitionId
      : undefined,
  preferredSide: isPropLabelPreferredSide(answer.preferredSide)
    ? answer.preferredSide
    : CreatorPropLabelPreferredSide.Auto,
  distanceBias: getDistanceBias(answer.distanceBias),
});

export const propLabelOrderQuizInteraction = (): InteractionFactoryEntity => {
  const view = <PropLabelOrderQuizPreview />;
  const filterType = "Prop Label Order Quiz";

  const create = (getKeys: (amount: number) => string[]): IPropLabelOrderQuiz => {
    const keys = getKeys(3);

    return {
      id: GetNewGuid(),
      name: "Prop Label Order Quiz",
      type: InteractionType.PropLabelOrderQuiz,
      title: keys[0],
      answers: [createAnswer(keys[1]), createAnswer(keys[2])],
      properties: [],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): IPropLabelOrderQuiz => {
    const casted = interaction as IPropLabelOrderQuiz;
    const answers =
      casted.answers?.map((answer) => migrateAnswer(answer, answer.text ?? "")) ??
      [];

    return {
      ...casted,
      type: InteractionType.PropLabelOrderQuiz,
      answers,
      properties: casted.properties ?? [],
    };
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
  ): IPropLabelOrderQuiz => {
    const quiz = interaction as IPropLabelOrderQuiz;
    const keys = getKeys(quiz.answers.length + 1);
    const title = keys[0];
    replaceKey(quiz.title, title);

    return {
      id: GetNewGuid(),
      name: quiz.name,
      type: InteractionType.PropLabelOrderQuiz,
      title,
      answers: quiz.answers.map((answer, index) => {
        const text = keys[index + 1];
        replaceKey(answer.text, text);
        return {
          ...answer,
          id: GetNewGuid(),
          correct: false,
          text,
        };
      }),
      properties: quiz.properties?.map((property) => ({ ...property })) ?? [],
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[],
  ): string[] => {
    const quiz = interaction as IPropLabelOrderQuiz;
    const warnings: string[] = [];

    ValidationHelper.ValidateKey(quiz.title, getValue, warnings, "Title");
    quiz.answers.forEach((answer, index) => {
      ValidationHelper.ValidateKey(
        answer.text,
        getValue,
        warnings,
        `Answer ${index + 1}`,
      );
    });

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
    getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const quiz = interaction as IPropLabelOrderQuiz;
    const searchableStrings = compactSearchableStrings(
      quiz.name,
      quiz.title,
      ...quiz.answers.flatMap((answer) => [
        answer.text,
        answer.tag,
        answer.preferredSide,
        answer.distanceBias,
        ...(searchLookupContext?.getPropSearchTerms(answer.propGuid) ?? [
          answer.propGuid,
        ]),
      ]),
    );

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType,
    );
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord,
  ): string[] => {
    const quiz = interaction as IPropLabelOrderQuiz;
    const errors: string[] = [];

    if (quiz.answers.length === 0) {
      errors.push("Quiz must have at least one answer");
    }

    quiz.answers.forEach((answer, index) => {
      if (!answer.propGuid) {
        errors.push(`Answer ${index + 1} requires a prop`);
      }
      if (!answer.tag) {
        errors.push(`Answer ${index + 1} requires a clickable tag`);
      }
    });

    return errors;
  };

  return {
    View: view,
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
